'use client';

import { FC, useEffect, useState } from 'react';

// import { zodResolver } from '@hookform/resolvers/zod';
import { TRPCClientError } from '@trpc/client';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
// import { z } from 'zod';

// import { email, employeeNumber } from 'backend/schema/auth';

import { getPreferredUsername } from 'backend/lib/aws/get_preferred_username';

import { Button } from '~/app/_components/Button';
import { PasswordInput } from '~/app/_components/PasswordInput';
import { TextInput } from '~/app/_components/TextInput';
import { useQueryParams } from '~/hooks/useQueryParams';
import { pagesPath } from '~/libs/$path';
import { trpc } from '~/libs/client/trpcQuery';
import { cookieKeys, getCookie } from '~/libs/cookies';
import { resolveShouldPromptPasswordResetAfterCognitoFailure } from '~/libs/login/should_prompt_password_reset';
import { useCognito } from '~/providers/cognito';
import {
  LOGIN_REDIRECT_QUERY_KEY,
  appendRedirectQuery,
  resolveSafeRedirectPath,
} from '~/utils/loginRedirect';

import ArrowRightIcon from '/public/icons/arrow-right.svg';
import Logo from '/public/logo.svg';

import styles from './style.module.css';

type FormValues = {
  password: string;
} & (
  | {
      email: string;
      employeeNumber: string | null;
      organizationId: string | null;
    }
  | {
      email: null;
      employeeNumber: string;
      organizationId: string;
    }
);

// const formValuesSchema: z.ZodType<FormValues> = z
//   .object({
//     password: z.string({
//       message: 'パスワードを入力してください',
//     }),
//   })
//   .and(
//     z.union([
//       z.object({
//         email: email,
//         employeeNumber: z.string().nullable(),
//         organizationId: z.string().nullable(),
//       }),
//       z.object({
//         email: z.null(),
//         employeeNumber: employeeNumber,
//         organizationId: z.string(),
//       }),
//     ]),
//   )
//   .refine((data) => data.email !== null || data.employeeNumber !== null, {
//     message: '社員番号かメールアドレスのいずれかは必須です',
//     path: ['email', 'employeeNumber'],
//   }) satisfies z.ZodType<FormValues>;

export type LoginFormProps = {
  type: 'email' | 'employeeId';
};

// NOTE: 忘れた場合に本人では確認できず、管理者に問い合わせるしかない識別子だけを案内する。
// パスワードは再設定の導線があるため案内に含めない。
const noteByLoginType: Record<LoginFormProps['type'], string> = {
  email:
    'メールアドレスを忘れてしまった場合は、会社の管理者の方にご連絡ください。',
  employeeId:
    '組織IDまたは社員番号を忘れてしまった場合は、会社の管理者の方にご連絡ください。',
};

const getLoginErrorMessage = (error: unknown): string => {
  // NOTE: 認証画面では、ユーザーの存在有無などの情報が推測できるメッセージは避ける（ユーザー列挙対策）。
  if (error instanceof TRPCClientError) {
    const { name, message } = error;

    // 例外型が安定しないケース向けにメッセージ文字列でも軽く判定する。
    const lowerMessage = message.toLowerCase();
    if (name === 'NetworkError' || lowerMessage?.includes('network')) {
      return '通信に失敗しました。ネットワーク状況をご確認ください';
    }
  }

  return 'メールアドレスまたはパスワードが間違っています';
};

export const LoginForm: FC<LoginFormProps> = ({ type }) => {
  const { queryParams } = useQueryParams<{ organizationId: string }>();
  const { cognito } = useCognito();
  const utils = trpc.useUtils();
  const router = useRouter();
  const { control, handleSubmit, setValue } = useForm<
    Omit<FormValues, 'email'> & { username: string }
  >({
    defaultValues: {
      username: '',
      employeeNumber: '',
      password: '',
      organizationId: '',
    },
    // resolver: zodResolver(formValuesSchema),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // 組織IDの自動入力
    // クエリパラメータがあれば優先（招待リンクなど外部からの遷移）
    if (queryParams.get('organizationId')) {
      setValue('organizationId', queryParams.get('organizationId'));
      return;
    }
    // Cookieから組織IDを自動入力する。
    // PWAで再ログインが必要になったときなどクエリパラメタ付きURL以外からの遷移時に使われる可能性あり
    const orgId = getCookie(cookieKeys.orgId);
    if (orgId) {
      setValue('organizationId', orgId);
    }
  }, [queryParams, setValue]);

  return (
    <div className={styles.loginForm}>
      <div className={styles.header}>
        <Logo className={styles.logo} />
      </div>

      <div className={styles.content}>
        <h1 className={styles.title}>
          {type === 'email' ? 'ログイン' : '社員番号ログイン'}
        </h1>

        <form
          className={styles.form}
          onSubmit={handleSubmit(async (data) => {
            try {
              setErrorMessage(null);
              setIsLoading(true);

              // NOTE: 未ログイン時に踏んだ元URL（安全な内部パスのみ）。パスワード変更や
              // アカウント初期設定を挟む初回ログイン動線でも引き継ぐ
              const redirectPath = resolveSafeRedirectPath(
                queryParams.get(LOGIN_REDIRECT_QUERY_KEY),
              );

              let userName;
              if (data.username) {
                userName = data.username;
              } else {
                if (!data.organizationId || !data.employeeNumber) {
                  throw new Error(
                    'organizationId and employeeNumber are required',
                  );
                }

                userName = getPreferredUsername({
                  organizationId: data.organizationId,
                  employeeNumber: data.employeeNumber,
                });
              }
              if (!userName) {
                throw new Error('email or employeeNumber is required');
              }

              const res = await cognito.authenticateUser(
                userName,
                data.password,
              );
              if (res.newPasswordRequired) {
                router.push(
                  appendRedirectQuery(
                    pagesPath.new_password.$url().path,
                    redirectPath,
                  ),
                );
                return;
              }
              const idToken = res.idToken;

              await utils.client.session.create.mutate({
                idToken,
              });

              // NOTE: 未ログイン時に踏んだ元URL（安全な内部パスのみ）があればそこへ復帰する
              router.push(redirectPath ?? pagesPath.$url().path);
            } catch (error) {
              // NOTE: ここは Cognito 認証失敗だけでなく、後続のセッション作成失敗も同じ catch に入る。
              // 入力ミス以外の失敗時に誤解を招くため、エラー種別でメッセージを出し分ける余地あり。
              const shouldPromptPasswordReset =
                await resolveShouldPromptPasswordResetAfterCognitoFailure(
                  error,
                  {
                    loginType: type,
                    username: data.username,
                    organizationId: data.organizationId,
                    employeeNumber: data.employeeNumber,
                    requestShouldPromptPasswordReset: (input) =>
                      utils.client.session.shouldPromptPasswordReset.mutate(
                        input,
                      ),
                  },
                );

              if (shouldPromptPasswordReset) {
                setErrorMessage(
                  '旧バージョンから移行されたアカウントのため、パスワードのリセットを管理者にご依頼ください',
                );
              } else {
                setErrorMessage(getLoginErrorMessage(error));
              }
            } finally {
              setIsLoading(false);
            }
          })}
        >
          {errorMessage && <p className={styles.error}>{errorMessage}</p>}
          <div className={styles.inputs}>
            {type === 'email' && (
              <TextInput
                label='メールアドレス'
                control={control}
                name='username'
              />
            )}
            {type === 'employeeId' && (
              <>
                <TextInput
                  label='組織ID'
                  control={control}
                  name='organizationId'
                  disabled={!!queryParams.get('organizationId')}
                />
                <TextInput
                  label='社員番号'
                  control={control}
                  name='employeeNumber'
                />
              </>
            )}
            <PasswordInput
              label='パスワード'
              control={control}
              name='password'
            />
          </div>

          <Button type='submit' isWide size='L' isLoading={isLoading}>
            ログイン
          </Button>
        </form>

        <div className={styles.links}>
          {type === 'email' && (
            <>
              <Link
                className={styles.link}
                href={pagesPath.login.organizationId.$url().pathname}
              >
                社員番号でログイン
                <ArrowRightIcon
                  className={styles.icon}
                  width={16}
                  height={16}
                />
              </Link>
              <Link
                className={styles.link}
                href={pagesPath.reset_password.$url().pathname}
              >
                パスワードが分からない場合
                <ArrowRightIcon
                  className={styles.icon}
                  width={16}
                  height={16}
                />
              </Link>
            </>
          )}
          {type === 'employeeId' && (
            <Link
              className={styles.link}
              href={pagesPath.login.$url().pathname}
            >
              メールアドレスでログイン
              <ArrowRightIcon className={styles.icon} width={16} height={16} />
            </Link>
          )}
          <Link
            className={styles.link}
            href='https://www.work-school.com/contact'
          >
            お問い合わせ
            <ArrowRightIcon className={styles.icon} width={16} height={16} />
          </Link>
          <p className={styles.note}>{noteByLoginType[type]}</p>
        </div>
      </div>
    </div>
  );
};
